import { useState, useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'

interface NavItem {
  path: string
  icon: string
  label: string
}

interface NavGroup {
  section: string
  sectionIcon: string
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    section: 'Telefonie',
    sectionIcon: 'bi-telephone-fill',
    items: [
      { path: '/extensions', icon: 'bi-telephone', label: 'Nebenstellen' },
      { path: '/trunks', icon: 'bi-diagram-3', label: 'Trunks' },
      { path: '/ring-groups', icon: 'bi-people', label: 'Rufgruppen' },
      { path: '/queues', icon: 'bi-list-ol', label: 'Warteschlangen' },
      { path: '/conferences', icon: 'bi-camera-video', label: 'Konferenzen' },
      { path: '/paging', icon: 'bi-megaphone', label: 'Durchsagen' },
      { path: '/parking', icon: 'bi-pause-circle', label: 'Parken' },
    ]
  },
  {
    section: 'Routing',
    sectionIcon: 'bi-signpost-split-fill',
    items: [
      { path: '/inbound-routes', icon: 'bi-box-arrow-in-right', label: 'Eingehend' },
      { path: '/outbound-routes', icon: 'bi-box-arrow-right', label: 'Ausgehend' },
      { path: '/ivr', icon: 'bi-menu-button-wide', label: 'IVR Menüs' },
      { path: '/time-conditions', icon: 'bi-clock', label: 'Zeitbedingungen' },
      { path: '/announcements', icon: 'bi-megaphone', label: 'Ansagen' },
      { path: '/night-mode', icon: 'bi-moon-stars', label: 'Nachtschaltung' },
      { path: '/blacklist', icon: 'bi-slash-circle', label: 'Blacklist' },
    ]
  },
  {
    section: 'Medien',
    sectionIcon: 'bi-music-note-list',
    items: [
      { path: '/recordings', icon: 'bi-mic', label: 'Aufnahmen' },
      { path: '/call-recording', icon: 'bi-record-circle', label: 'Gesprächsaufnahme' },
      { path: '/voicemail', icon: 'bi-voicemail', label: 'Voicemail' },
      { path: '/fax', icon: 'bi-printer', label: 'Fax' },
      { path: '/music-on-hold', icon: 'bi-music-note-beamed', label: 'Wartemusik' },
      { path: '/cdr', icon: 'bi-journal-text', label: 'Anrufhistorie' },
    ]
  },
  {
    section: 'Verzeichnisse',
    sectionIcon: 'bi-book-fill',
    items: [
      { path: '/phonebook', icon: 'bi-person-lines-fill', label: 'Telefonbuch' },
      { path: '/external-destinations', icon: 'bi-telephone-outbound-fill', label: 'Externe Ziele' },
    ]
  },
  {
    section: 'System',
    sectionIcon: 'bi-gear-fill',
    items: [
      { path: '/provisioning', icon: 'bi-phone', label: 'Telefon-Provisioning' },
      { path: '/softphone-provisioning', icon: 'bi-headset', label: 'it64PbxPhone' },
      { path: '/firmware', icon: 'bi-download', label: 'Firmware' },
      { path: '/ami-users', icon: 'bi-terminal', label: 'AMI-Benutzer' },
      { path: '/feature-codes', icon: 'bi-hash', label: 'Feature-Codes' },
      { path: '/backup', icon: 'bi-cloud-arrow-down', label: 'DB-Backup' },
      { path: '/system-backups', icon: 'bi-hdd', label: 'System-Backup' },
      { path: '/wizard', icon: 'bi-magic', label: 'Assistent' },
      { path: '/settings', icon: 'bi-gear', label: 'Einstellungen' },
    ]
  },
]

const adminGroups: NavGroup[] = [
  {
    section: 'Administration',
    sectionIcon: 'bi-shield-lock-fill',
    items: [
      { path: '/tenants', icon: 'bi-building', label: 'Mandanten' },
      { path: '/users', icon: 'bi-person-badge', label: 'Benutzer' },
      { path: '/fail2ban', icon: 'bi-shield-lock', label: 'Fail2Ban' },
      { path: '/logs', icon: 'bi-journal-code', label: 'Asterisk Logs' },
      { path: '/global-settings', icon: 'bi-sliders', label: 'System-Einstellungen' },
    ]
  }
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const user = useAuthStore(state => state.user)
  const location = useLocation()
  const isSuperAdmin = user?.role === 'super_admin'

  const allGroups = isSuperAdmin ? [...navGroups, ...adminGroups] : navGroups

  // Track which single section is expanded (accordion behavior)
  const [expandedSection, setExpandedSection] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem('sidebar-expanded-section')
      if (saved) {
        return saved
      }
    } catch {}
    return null
  })

  // Auto-expand section containing active route
  useEffect(() => {
    const currentPath = location.pathname
    for (const group of allGroups) {
      if (group.items.some(item => item.path === currentPath)) {
        setExpandedSection(group.section)
        break
      }
    }
  }, [location.pathname])

  // Save to localStorage when expanded state changes
  useEffect(() => {
    if (expandedSection) {
      localStorage.setItem('sidebar-expanded-section', expandedSection)
    } else {
      localStorage.removeItem('sidebar-expanded-section')
    }
  }, [expandedSection])

  // Accordion toggle: only one section open at a time
  const toggleSection = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section)
  }

  // Helper to check if section is expanded
  const expandedSections: Record<string, boolean> = {}
  if (expandedSection) {
    expandedSections[expandedSection] = true
  }

  const isSectionActive = (group: NavGroup) => {
    return group.items.some(item => location.pathname === item.path)
  }

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-brand">
        <i className="bi bi-headset"></i>
        <span>it64Pbx</span>
        {/* Close button for mobile */}
        <button className="btn btn-link d-md-none ms-auto p-0 text-white" onClick={onClose}>
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
      <nav className="sidebar-nav">
        {/* Dashboard - always visible, not in a group */}
        <NavLink
          to="/"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          end
        >
          <i className="bi bi-speedometer2"></i>
          <span>Dashboard</span>
        </NavLink>

        {/* Collapsible sections */}
        {allGroups.map((group) => (
          <div key={group.section} className="nav-group">
            <div
              className={`nav-section-header ${isSectionActive(group) ? 'active' : ''}`}
              onClick={() => toggleSection(group.section)}
            >
              <div className="nav-section-title">
                <i className={`bi ${group.sectionIcon}`}></i>
                <span>{group.section}</span>
              </div>
              <i className={`bi ${expandedSections[group.section] ? 'bi-chevron-down' : 'bi-chevron-right'} nav-section-chevron`}></i>
            </div>

            {expandedSections[group.section] && (
              <div className="nav-section-items">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => `nav-item nav-item-nested ${isActive ? 'active' : ''}`}
                  >
                    <i className={`bi ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </aside>
  )
}
